import React, {useEffect, useState} from 'react';
import {useHistory} from "react-router";
import styled from 'styled-components';

import {useActionApi, useApi, usePersistedState} from "hooks";
import {entities} from "reduxStore";
import Api from "api";

import {BackendJob, EditedJob, createEditedJob} from "components/EditJob";
import {TopBarRedesigned} from "components/TopBarRedesigned";
import {JobBoardDetails, Application} from "components/JobBoardDetails";
import {Location} from "components/BillingLocationSelector";
import Popup from 'components/Popup'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import {DeleteJobConfirmation} from 'components/JobBoardDetails/DeleteJobConfirmation'
import { nativePageShell, nativePageScrollRegion } from 'styles/nativePageLayout';

const PageWrapper = styled.div`
  ${nativePageShell}
`;

const PageScroll = styled.div`
  ${nativePageScrollRegion}
`;

const FeatureCard = styled.div`
  border: 1px solid var(--primary-blue, #0B4598);
  border-radius: 8px;
  padding: 12px;
  margin: 12px auto;
  max-width: 900px;
  width: calc(100% - 24px);
  background: #F0F9FF;
  box-sizing: border-box;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  max-width: 900px;
  width: calc(100% - 24px);
  margin: 16px auto 24px;

  @media (max-width: 600px) {
    /* Keep the final actions above the persistent help bubble on phones. */
    margin-bottom: 96px;
  }

  button {
    border: 0;
    background: transparent;
    color: #0B4598;
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
    padding: 8px;
  }
`;

const JobBoardDetailsPage = () => {
    const history = useHistory();

    const [confirmDeletePopupOpened, setConfirmDeletePopupOpened] = useState(false);
    const [featureStatus, setFeatureStatus] = useState<{ isFeatured: boolean; status: string } | null>(null);
    const [availability, setAvailability] = useState<{ status: 'available'|'queued'|'unavailable'; activeCount: number; jobType: number }|null>(null);
    const [featureLoading, setFeatureLoading] = useState(false);

    const { job } = history.location.state as { job: BackendJob }

    const billingLocations = usePersistedState({
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })[0] as Location[]

    const setJobToEdit = usePersistedState({
        entityName: entities.editedJobPost,
        initialValue: job as EditedJob,
    })[1]

    const setApplicationToView = usePersistedState({
        entityName: entities.dentistCandidateApplication,
    })[1]

    const [jobCandidates, loading, error, getCandidates] = useApi({
        apiMethod: Api.getApplicationsForHireJob,
        initialValue: [],
    })

    const [loadingDelete, errorDelete, deleteJob] = useActionApi({
        apiMethod: Api.deleteJob
    })

    useEffect(() => {
        getCandidates(job.id as number)
    }, [job.id, getCandidates])

    useEffect(() => {
        (async () => {
            try {
                await Api.updateToken();
                const [st, av] = await Promise.all([
                    Api.getFeatureStatus(job.id as number).catch(() => null),
                    Api.getFeatureAvailability(job.id as number).catch(() => null)
                ]);
                if (st) setFeatureStatus(st);
                if (av) setAvailability(av as any);
            } catch (e) {}
        })();
    }, [job.id])

    const onApplicationClick = (application: Application) => {
        setApplicationToView(application)
        history.push({
            pathname: 'candidate',
            state: {
                job,
            },
        })
    }

    const onJobEditClick = () => {
        setJobToEdit(createEditedJob(job));

        history.push({
            pathname: "editJobOverviewStep",
            state: { mode: "edit", originalJob: job }
        })
    }

    const onJobDeleteClick = () => {
        setConfirmDeletePopupOpened(true)
    }

    const onJobDeleteConfirmClick = () => {
        if (job.status === 0) {
            deleteJob(job.id as number)

            history.push({
                pathname: 'deleteSuccess'
            })
        } else {
            history.push({
                pathname: 'jobFeedback',
                state: {
                    job,
                },
            })
        }
    }

    const onMarkFilledClick = async () => {
        if (!window.confirm('Mark this position as filled? This will stop new applications and free up any featured slot.')) {
            return;
        }
        
        try {
            await Api.markJobFilled(job.id as number);
            alert('Position marked as filled successfully!');
            history.replace('jobBoard');
        } catch (err: any) {
            alert(`Failed to mark as filled: ${err.message || 'Unknown error'}`);
        }
    }

    return (
        <PageWrapper>
            <TopBarRedesigned
                title='Job Details'
                onBack={() => history.replace('jobBoard')}
                rightActionLabel="Edit my Post"
                rightActionTestId="job-board-edit-my-post"
                onRightAction={onJobEditClick}
            />
            <PageScroll>
            {/* Feature Section */}
            <FeatureCard>
                <div style={{ fontWeight: 700, color: 'var(--primary-blue, #0B4598)', marginBottom: 4 }}>⭐ Feature this listing</div>
                {featureStatus?.isFeatured ? (
                    <div style={{ marginBottom: 6 }}>
                        {featureStatus.status === 'in_rotation' && 'Featured — Payment will occur when you view your first candidate (token spend).'}
                        {featureStatus.status === 'queued' && 'Feature Waitlist — Your listing will be featured automatically when a slot opens. No charge until you view your first candidate.'}
                        {featureStatus.status === 'charged' && 'Featured — Charge processed after viewing first candidate.'}
                        {(featureStatus.status === 'expired' || featureStatus.status === 'deleted' || featureStatus.status === 'filled') && 'Feature Ended — Listing is no longer active.'}
                    </div>
                ) : (
                    <div style={{ marginBottom: 6 }}>
                        {availability?.status === 'available' && '✓ Feature slot available — Feature your listing immediately. $25 + HST, charged on first candidate view.'}
                        {availability?.status === 'queued' && '📋 Featured Listing slots are filled — Join the waitlist. No charge until your slot opens and you view your first candidate.'}
                        {availability?.status === 'unavailable' && 'Feature unavailable for this listing (Ontario-only)'}
                    </div>
                )}
                <ActionsRow>
                    {!featureStatus?.isFeatured && availability?.status !== 'unavailable' && (
                        <button
                            className="button button-fill color-blue"
                            disabled={featureLoading}
                            onClick={async () => {
                                try {
                                    setFeatureLoading(true);
                                    await Api.updateToken();
                                    const res = await Api.featureListing(job.id as number);
                                    setFeatureStatus({ isFeatured: true, status: res.status });
                                } catch (e) {} finally { setFeatureLoading(false); }
                            }}
                        >
                            {availability?.status === 'queued' ? 'Join Feature Waitlist' : 'Feature Now ($25 + HST)'}
                        </button>
                    )}
                    {featureStatus?.isFeatured && (
                        <button
                            className="button button-outline color-blue"
                            disabled={featureLoading}
                            onClick={async () => {
                                try {
                                    setFeatureLoading(true);
                                    await Api.updateToken();
                                    await Api.unfeatureListing(job.id as number);
                                    setFeatureStatus({ isFeatured: false, status: 'none' });
                                } catch (e) {} finally { setFeatureLoading(false); }
                            }}
                        >
                            Disable Feature
                        </button>
                    )}
                </ActionsRow>
            </FeatureCard>
            <JobBoardDetails
                job={job}
                locations={billingLocations}
                applications={jobCandidates}
                onApplicationClick={onApplicationClick}
            />
            <FooterActions>
                <button type="button" data-testid="job-board-mark-position-filled" onClick={onMarkFilledClick}>
                    Mark Position as Filled
                </button>
                <button type="button" data-testid="job-board-delete-job" onClick={onJobDeleteClick}>
                    Delete Job
                </button>
            </FooterActions>
            </PageScroll>
            <Popup
                opened={confirmDeletePopupOpened}
                onClose={() => setConfirmDeletePopupOpened(false)}
            >
                <DeleteJobConfirmation
                    hardDelete={job.status === 0}
                    onCancel={() => setConfirmDeletePopupOpened(false)}
                    onDelete={onJobDeleteConfirmClick}
                />
            </Popup>
            <FixedLoader loading={loading || loadingDelete} />
            <FixedError error={error || errorDelete} />
        </PageWrapper>
    )
}

export default JobBoardDetailsPage;
